<script lang="ts" setup>
  import type { IHeroWide } from '@/components/hero/types'
  import { cva } from 'class-variance-authority'
  import useIndexPageDataLayer from '@/composables/data-layers/useIndexPageDataLayer'
  import { getCompactLinkData } from '@/utils/storyblok/getCompactLinkData'

  const props = defineProps<{
    item: IHeroWide
  }>()

  const dayjs = useDayjs()

  const isCampaign = computed(() => {
    if (!props.item.bookFlightFrom || !props.item.bookFlightTo) return false

    const currentDate = dayjs()
    const bookFlightFrom = dayjs(props.item.bookFlightFrom).startOf('day')
    const bookFlightTo = dayjs(props.item.bookFlightTo).endOf('day')

    return currentDate.isBetween(bookFlightFrom, bookFlightTo, 'hour', '[]')
  })

  const { isDesktopLarge, isDesktopSmall, isTablet } = useBTBreakpoints()

  const backgroundImage = computed(() => {
    if (isCampaign.value) {
      return useResponsiveAsset(
        props.item?.campaignImageMobile,
        props.item?.campaignImageTablet,
        props.item?.campaignImageDesktop,
        props.item?.campaignImageDesktopWide
      )
    }

    return useResponsiveAsset(
      props.item?.mobileImage,
      props.item?.tabletImage,
      props.item?.desktopImage,
      props.item?.wideDesktopImage
    )
  })

  const backgroundImageSize = computed(() => {
    if (isDesktopLarge.value) {
      if (isOverlapVariant.value) {
        return {
          width: 1840,
          height: 400
        }
      }

      return {
        width: 1840,
        height: 800
      }
    }

    if (isDesktopSmall.value) {
      if (isOverlapVariant.value) {
        return {
          width: 1320,
          height: 400
        }
      }

      return {
        width: 1320,
        height: 600
      }
    }

    if (isTablet.value) {
      return {
        width: 768,
        height: 626
      }
    }

    return {
      width: 360,
      height: 586
    }
  })

  const nuxtImage = useImage()
  const bgImageStyle = computed(() => {
    if (!backgroundImage.value) {
      return ''
    }

    const bgImage = nuxtImage(
      backgroundImage.value.filename ?? '',
      {
        height: backgroundImageSize.value.height,
        width: backgroundImageSize.value.width,
        quality: 80,
        preload: true,
        filters: {
          fileType: 'webp',
          focal: backgroundImage.value?.focus
        }
      },
      { provider: 'storyblok' }
    )

    return `background: url('${bgImage}') center center / cover no-repeat;`
  })

  const textStyle = computed(() =>
    cva('', {
      variants: {
        textColor: {
          default: 'text-inherit',
          white: 'text-white'
        }
      },
      defaultVariants: {
        textColor: props.item.textColor || 'default'
      }
    })()
  )

  const { pushFormPromoBannerDataLayer } = useIndexPageDataLayer()

  const backgroundImageHref = computed(() => {
    return isCampaign.value
      ? getCompactLinkData(props.item.campaignBackgroundImageHref)
      : getCompactLinkData(props.item.backgroundImageHref)
  })

  const onBackgroundClick = async () => {
    if (!backgroundImageHref.value.href) {
      return
    }

    pushFormPromoBannerDataLayer({
      event_label: backgroundImageHref.value.name
    })

    await navigateTo(backgroundImageHref.value.href, {
      external: backgroundImageHref.value.external
    })
  }

  const title = computed(() => {
    return isCampaign.value ? props.item.campaignTitle : props.item.title
  })

  const description = computed(() => {
    return isCampaign.value ? props.item.campaignDescription : props.item.description
  })

  const route = useRoute()
  const pageType = computed(() => (typeof route.meta.pageType === 'string' ? route.meta.pageType : ''))
  const isIndexPage = computed(() => pageType.value === 'index')
  const isOverlapVariant = computed(() => isIndexPage.value)

  const fsfVariantClass = computed(() =>
    cva(
      'relative m-auto flex w-full max-w-460 flex-col rounded-md bg-cover bg-center px-5 pb-16 pt-6 md:justify-between md:px-24 md:pt-10 xl:px-53',
      {
        variants: {
          variant: {
            default: 'md:h-135 xl:h-160 xl:pb-31 md:pb-18',
            overlap: 'md:mb-36 md:pb-24 md:h-100 xl:!h-100 xl:!pb-24'
          }
        },
        defaultVariants: {
          variant: isOverlapVariant.value ? 'overlap' : 'default'
        }
      }
    )()
  )
</script>

<template>
  <div
    :key="`hero-${isOverlapVariant ? 'overlap' : 'default'}`"
    :class="fsfVariantClass"
    :style="bgImageStyle"
  >
    <NuxtLink
      v-if="backgroundImageHref.href"
      :to="backgroundImageHref.href"
      class="absolute md:inset-0"
      :aria-labelledby="item.backgroundHrefAriaLabelledBy"
      @click.prevent="onBackgroundClick"
    />

    <div
      class="flex h-full flex-col"
      :class="{ 'md:static md:-mb-36': isOverlapVariant }"
    >
      <h2
        v-if="title"
        class="pointer-events-none m-0 px-5 py-6 text-center text-h2 md:hidden"
        :class="textStyle"
      >
        {{ title }}
      </h2>

      <div
        v-if="!isOverlapVariant"
        class="md:relative"
      >
        <slot />
      </div>

      <div
        v-if="title"
        class="pointer-events-none mt-6 hidden h-full w-138 flex-col justify-end gap-6 md:flex xl:mt-8 xl:w-204 xl:gap-8"
        :class="textStyle"
      >
        <h2 class="m-0 text-h2">{{ title }}</h2>

        <div
          v-if="description"
          class="text-body-large"
        >
          {{ description }}
        </div>

        <div
          v-if="item.ctaText"
          class="flex items-center gap-2"
        >
          <span class="text-subtitle-1">{{ item.ctaText }}</span>
          <span class="text-bt-green">
            <Icon name="common/chevron-right" />
          </span>
        </div>
      </div>
    </div>
    <div
      v-if="isOverlapVariant"
      class="md:relative md:-bottom-52 md:rounded-md md:shadow-lg"
    >
      <slot />
    </div>
  </div>
</template>
